import { Flex, Text } from "rebass";
import styled from "styled-components";
import { FaSteam } from "react-icons/fa";
import { SiBattledotnet, SiEpicgames, SiRiotgames } from "react-icons/si";
import { RiTiktokFill } from "react-icons/ri";
import { BiPlus } from "react-icons/bi";
import React from "react";

import { IUserProfile } from "MVVM/@types";
import { StudioIcon } from "MVVM/Views/Studio/Components/StudioIcons";
import { EStudioIconType } from "MVVM/Views/Studio/@types";
import { Link } from "MVVM/Components/Link";
import YoutubeIcon from "components/atoms/icons/youtube";
import TwitchIcon from "components/atoms/icons/twitch";
import TwitterIcon from "components/atoms/icons/twitter";
import InstagramIcon from "components/atoms/icons/instagram";

interface ISocialPillsProps {
  user: IUserProfile;
  variant?: "small" | "default";
  onAddSocialClick?: () => void;
}

interface IPill {
  name: string;
  icon: React.JSX.Element;
  colorKey: string;
  href?: string;
  textColor?: string;
}

const getSocialPills = (user: IUserProfile) => {
  const socialPlatforms = [
    {
      data: user.steam?.personaname,
      icon: <FaSteam />,
      colorKey: "steam",
      href: user.steam?.steamid
        ? `https://steamcommunity.com/profiles/${user.steam.steamid}`
        : undefined,
    },
    { data: user.riot?.riotId, icon: <SiRiotgames />, colorKey: "riotGames" },
    {
      data: user.epic?.displayName,
      icon: <SiEpicgames />,
      colorKey: "epicGames",
    },
    {
      data: user.battlenet?.battletag,
      icon: <SiBattledotnet />,
      colorKey: "battleNet",
    },
    {
      data: user.profile?.profile?.socials?.youtube,
      icon: <YoutubeIcon />,
      colorKey: "youtube",
      href: `https://www.youtube.com/@${user.profile?.profile?.socials?.youtube}`,
    },
    {
      data: user.profile?.profile?.socials?.twitch,
      icon: <TwitchIcon />,
      colorKey: "twitch",
      href: `https://www.twitch.tv/${user.profile?.profile?.socials?.twitch}`,
    },
    {
      data: user.profile?.profile?.socials?.tiktok,
      icon: <RiTiktokFill />,
      colorKey: "tiktok",
      href: `https://www.tiktok.com/@${user.profile?.profile?.socials?.tiktok}`,
    },
    {
      data: user.profile?.profile?.socials?.twitter,
      icon: <TwitterIcon />,
      colorKey: "twitter",
      href: `https://www.twitter.com/${user.profile?.profile?.socials?.twitter}`,
    },
    {
      data: user.profile?.profile?.socials?.instagram,
      icon: <InstagramIcon />,
      colorKey: "instagram",
      href: `https://www.instagram.com/${user.profile?.profile?.socials?.instagram}`,
    },
  ];

  const pills: IPill[] = socialPlatforms
    .filter((platform) => platform.data)
    .map((platform) => ({
      name: platform.data!,
      icon: platform.icon,
      colorKey: platform.colorKey,
      href: platform.href,
    }));

  return pills;
};

const getPlayedGamesPills = (user: IUserProfile) => {
  if (!user.playedGames) return [];

  const supportedGames = {
    CS2: {
      name: "Counter-Strike 2",
      icon: <StudioIcon size={20} type={EStudioIconType.CS2} />,
    },
    FORTNITE: {
      name: "Fortnite",
      icon: <StudioIcon size={20} type={EStudioIconType.Fortnite} />,
    },
    LOL: {
      name: "League of Legends",
      icon: <StudioIcon size={20} type={EStudioIconType.LoL} />,
    },
    DOTA2: {
      name: "Dota 2",
      icon: <StudioIcon size={20} type={EStudioIconType.Dota2} />,
    },
    OW2: {
      name: "Overwatch 2",
      icon: <StudioIcon size={20} type={EStudioIconType.OW2} />,
    },
  };

  return user.playedGames
    .filter((game) => game in supportedGames)
    .map((game) => {
      const supportedGame = supportedGames[game as keyof typeof supportedGames];
      return {
        name: supportedGame.name,
        icon: supportedGame.icon,
        colorKey: "navy50",
      };
    });
};

export const SocialPills = ({
  user,
  variant = "default",
  onAddSocialClick,
}: ISocialPillsProps) => {
  const socialPills = getSocialPills(user);
  const playedGamesPills = getPlayedGamesPills(user);
  const isSmall = variant === "small";

  return (
    <SocialPillsContainer mt={isSmall ? 0 : 3} variant={variant}>
      {socialPills.map((pill) =>
        pill.href ? (
          <SocialPillLink
            href={pill.href}
            target="_blank"
            rel="noopener noreferrer"
            key={pill.colorKey}
          >
            <SocialPill
              platformColorKey={pill.colorKey}
              textColor={pill.textColor}
              hasHref={true}
              variant={variant}
            >
              <Flex>
                <IconWrapper
                  className="icon"
                  mr={isSmall ? 1 : 2}
                  variant={variant}
                >
                  {pill.icon}
                </IconWrapper>
                <Text paddingRight={"4px"} fontSize={isSmall ? 12 : 14}>
                  {pill.name}
                </Text>
              </Flex>
            </SocialPill>
          </SocialPillLink>
        ) : (
          <SocialPill
            platformColorKey={pill.colorKey}
            textColor={pill.textColor}
            hasHref={false}
            key={pill.colorKey}
            variant={variant}
          >
            <Flex>
              <IconWrapper
                className="icon"
                mr={isSmall ? 1 : 2}
                variant={variant}
              >
                {pill.icon}
              </IconWrapper>
              <Text paddingRight={"4px"} fontSize={isSmall ? 12 : 14}>
                {pill.name}
              </Text>
            </Flex>
          </SocialPill>
        ),
      )}

      {playedGamesPills.map((pill) => (
        <ResponsiveGamePill
          platformColorKey={pill.colorKey}
          key={pill.name}
          variant={variant}
        >
          <Flex>
            <IconWrapper className="icon" variant={variant}>
              {pill.icon}
            </IconWrapper>
            <Text paddingRight={"4px"} fontSize={isSmall ? 12 : 14}>
              {pill.name}
            </Text>
          </Flex>
        </ResponsiveGamePill>
      ))}

      {user.isOwnProfile && (
        <AddSocialButton onClick={onAddSocialClick}>
          <SocialPill
            hasHref={true}
            platformColorKey="navy200"
            textColor="shade400"
            variant={variant}
          >
            <Flex>
              <IconWrapper
                className="icon"
                mr={isSmall ? 1 : 2}
                variant={variant}
              >
                <BiPlus />
              </IconWrapper>
              <Text paddingRight={"4px"} fontSize={isSmall ? 12 : 14}>
                Add Social Profile
              </Text>
            </Flex>
          </SocialPill>
        </AddSocialButton>
      )}
    </SocialPillsContainer>
  );
};

const IconWrapper = styled(Flex)<{ variant?: string }>`
  svg {
    color: ${({ theme }) => theme.colors.shade300};
    height: ${({ variant }) => (variant === "small" ? "16px" : "20px")};
    transition: color 0.2s ease;
    width: ${({ variant }) => (variant === "small" ? "16px" : "20px")};
  }
`;

const SocialPillsContainer = styled(Flex)<{ variant?: string }>`
  flex-wrap: wrap;
  gap: ${({ variant }) => (variant === "small" ? "6px" : "8px")};
  max-width: ${({ variant }) => (variant === "small" ? "100%" : "500px")};

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const SocialPillLink = styled(Link)`
  text-decoration: none;
`;

const AddSocialButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  text-decoration: none;

  &:hover {
    cursor: pointer;
  }
`;

const SocialPill = styled(Flex)<{
  platformColorKey: string;
  textColor?: string;
  hasHref?: boolean;
  variant?: string;
}>`
  &:hover {
    background-color: ${({ theme, platformColorKey }) =>
      theme.colors[platformColorKey]};
    border-color: ${({ theme }) => theme.colors.navy500};
    color: #ffffff;
  }

  &:hover .icon svg {
    color: #ffffff;
  }

  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.shade400};
  border-radius: 999px;
  color: ${({ textColor, theme }) =>
    textColor ? theme.colors[textColor] : theme.colors.ice};
  cursor: ${({ hasHref }) => (hasHref ? "pointer" : "default")};
  font-family: Barlow;
  font-weight: 500;
  gap: ${({ variant }) => (variant === "small" ? "6px" : "8px")};
  height: ${({ variant }) => (variant === "small" ? "28px" : "36px")};
  justify-content: center;
  min-width: ${({ variant }) => (variant === "small" ? "auto" : "142px")};
  padding-left: ${({ variant }) => (variant === "small" ? "6px" : "8px")};
  padding-right: ${({ variant }) => (variant === "small" ? "6px" : "8px")};
  transition:
    border-color 0.2s ease,
    transform 0.2s ease;
  width: auto;

  .icon svg {
    color: ${({ textColor, theme }) =>
      textColor ? theme.colors[textColor] : theme.colors.shade300};
  }
`;

const ResponsiveGamePill = styled(SocialPill)<{
  platformColorKey: string;
  variant?: string;
}>`
  display: none !important;

  @media (max-width: 1650px) {
    display: flex !important;
  }
`;
